import DeliveryFormV3 from "@components/Delivery/DeliveryFormV3";
import { useUserContext } from "@contexts/UserContext/index.context";
import { useQueryClient } from "@tanstack/react-query";
import type { DeliveryType as DeliveryTypeEnum } from "@types_def/models/delivery.types";
import { UserRole } from "@types_def/models/user.type";
import { useTranslation } from "react-i18next";
import type { Params } from "react-router-dom";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { invalidateDeliveryCache, invalidateDeliveryListCaches } from "@/features/delivery/cache";
import { useFindDeliveryByIdQuery, useUpdateDeliveryMutation } from "@/libs/generated/hooks";
import { enumToStr } from "@/utils/strEnum";

function EditDeliveryV3() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { type, contract, status } = useParams<Params>();

  const [searchParams] = useSearchParams();
  const stepParam = searchParams.get("step");

  if (!(type && contract && status)) {
    throw new Error("Type, contract, or status is not defined");
  }

  const deliveryType = type;

  const {
    data: graphQlData,
    isPending: isLoading,
    error,
  } = useFindDeliveryByIdQuery({
    GetByIdInput: { id: Number(contract) },
  });
  const data = graphQlData?.findDeliveryById;

  const { userState } = useUserContext();
  if (!userState) {
    throw new Error("Missing userState");
  }

  const isAdmin = userState["custom:role"] === UserRole.ADMIN;
  const formId = `edit-delivery-form-v3_${type}_${status}_${data?.id}`;

  const updateDelivery = useUpdateDeliveryMutation({
    onSuccess: async () => {
      if (data?.id) {
        await invalidateDeliveryCache(queryClient, data.id);
        await invalidateDeliveryListCaches(queryClient);
        const deliverySrtType = enumToStr<DeliveryTypeEnum>(data.type);
        if (isAdmin) {
          navigate(`/admin/pilot/${deliverySrtType}/confirmed/${data.id}`, { replace: true });
        } else {
          navigate(`/client/pilot/${deliverySrtType}/confirmed/${data.id}`, { replace: true });
        }
      }
    },
    onError: (error) => {
      console.error("Error updating delivery:", error);
    },
  });

  const handleSubmit = (formData: Record<string, unknown>) => {
    if (data?.id) {
      updateDelivery.mutate({ updateInput: formData });
    }
  };

  if (isLoading) {
    return <span>Loading...</span>;
  }
  if (error) {
    return <span>Error: {error?.message}</span>;
  }
  if (!data) {
    return <span>No data</span>;
  }

  return (
    <DeliveryFormV3
      type={deliveryType}
      formId={formId}
      data={data}
      title={`${t("common.shared.edit-delivery")} ${data?.id}`}
      initialStep={stepParam ? Number.parseInt(stepParam, 10) : 0}
      onSubmit={handleSubmit}
    />
  );
}

export default EditDeliveryV3;
